if (!customElements.get('search-drawer')) {
  class SearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.drawer = null;
      this.overlay = null;
      this.closeButton = null;
      this.triggerButtons = [];
      this.isOpen = false;
      this.searchHandler = null;
    }

    connectedCallback() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        setTimeout(() => this.init(), 0);
      }
    }

    init() {
      // Find elements within the component or document
      this.drawer = this.querySelector('.search-drawer') || document.querySelector('.search-drawer');
      
      if (!this.drawer) {
        console.error('Search drawer element not found');
        return;
      }

      this.overlay = this.drawer.querySelector('.search-overlay');
      this.closeButton = this.drawer.querySelector('.search-close-button');
      
      // Find all trigger buttons (can be multiple) - use event delegation for dynamic buttons
      this.triggerButtons = document.querySelectorAll('.show-search-drawer');
      
      // Also listen for clicks on the document for dynamically added buttons
      document.addEventListener('click', this.handleDocumentClick.bind(this));
      
      // Find search-handler component
      this.searchHandler = this.drawer.querySelector('search-handler');
      
      // Set up event listeners
      this.setupEventListeners();
    }

    disconnectedCallback() {
      // Clean up event listeners
      this.removeEventListeners();
    }

    handleDocumentClick(event) {
      // Handle clicks on dynamically added buttons
      const triggerButton = event.target.closest('.show-search-drawer');
      if (triggerButton) {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      }
    }

    setupEventListeners() {
      // Add click listeners to all trigger buttons
      this.triggerButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.open();
        });
      });

      // Close button listener
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.close.bind(this));
      }

      // Overlay click listener
      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      // ESC key listener
      document.addEventListener('keydown', this.handleKeyDown.bind(this));
    }

    removeEventListeners() {
      this.triggerButtons.forEach(button => {
        button.removeEventListener('click', this.open.bind(this));
      });

      document.removeEventListener('click', this.handleDocumentClick.bind(this));

      if (this.closeButton) {
        this.closeButton.removeEventListener('click', this.close.bind(this));
      }

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.close.bind(this));
      }

      document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }

    open() {
      if (!this.drawer || this.isOpen) return;

      this.isOpen = true;
      this.drawer.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
      
      // Focus search input after drawer opens
      if (this.searchHandler) {
        const searchInput = this.searchHandler.querySelector('#search-input') || 
                          this.searchHandler.querySelector('input[type="search"]') ||
                          this.drawer.querySelector('input[type="search"]');
        if (searchInput) {
          setTimeout(() => {
            searchInput.focus();
          }, 100);
        }
      }
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('search-drawer:open', {
        bubbles: true,
        detail: { drawer: this.drawer }
      }));
    }

    close() {
      if (!this.drawer || !this.isOpen) return;

      this.isOpen = false;
      this.drawer.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
      
      // Clear search when closing drawer
      if (this.searchHandler && typeof this.searchHandler.clear === 'function') {
        this.searchHandler.clear();
      }
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('search-drawer:close', {
        bubbles: true,
        detail: { drawer: this.drawer }
      }));
    }

    // Public methods for external control
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }
  customElements.define('search-drawer', SearchDrawer);
}